import { useState, useEffect, useRef } from 'react';
import TileContent from './tiles/TileContent.jsx';
import TextTile from './tiles/TextTile.jsx';
import ErrorBoundary from './ErrorBoundary.jsx';

// One tile of a SHARED report. Used by the public page (/r/:token) and by the
// owner's preview, so what the sender checks is the same component — and the
// same live query — the reader gets.
//
// It never holds a query: `load(tileId)` fetches by ID and the server owns what
// that means. Stale numbers stay on screen, dimmed, while the next result lands,
// so a refresh doesn't flash the report back to skeletons.
export default function SharedTile({ tile, load, refreshKey = 0, onFetched }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(tile.type !== 'text' && tile.runnable);
  const [error, setError] = useState('');
  const hasData = useRef(false);

  useEffect(() => {
    if (tile.type === 'text' || !tile.runnable) { setLoading(false); return undefined; }
    let alive = true;
    if (!hasData.current) setLoading(true);
    load(tile.id)
      .then((d) => {
        if (!alive) return;
        setData(d); hasData.current = true; setError('');
        if (d.fetchedAt) onFetched?.(d.fetchedAt);
      })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile.id, tile.type, tile.runnable, refreshKey]);

  const vt = tile.vis?.type || '';
  const isMetric = vt === 'single_value' || vt === 'single_value_period_over_period' || vt.includes('bar_gauge');
  const showHeader = !isMetric && !!tile.title;

  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md, 14px)', boxShadow: 'var(--shadow-sm)', height: '100%', minHeight: 110, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {showHeader && (
        <div style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, borderBottom: '1px solid var(--border)', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tile.title}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', padding: tile.type === 'text' ? 12 : 0 }}>
        {tile.type === 'text' ? <TextTile tile={tile} />
          : error ? <Centred>⚠ {error}</Centred>
            : loading && !data ? <Centred faint>Loading…</Centred>
              : data ? (
                <div style={{ height: '100%', opacity: loading ? 0.55 : 1, transition: 'opacity .2s ease' }}>
                  <ErrorBoundary resetKey={data}><TileContent tile={tile} data={data} /></ErrorBoundary>
                </div>
              ) : <Centred faint>No data</Centred>}
      </div>
    </div>
  );
}

function Centred({ children, faint }) {
  return <div style={{ height: '100%', minHeight: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14, textAlign: 'center', fontSize: 13, color: faint ? 'var(--muted)' : 'var(--text)' }}>{children}</div>;
}
